import { Link } from '@tanstack/react-router';
import { Image } from '@unpic/react';
import { useState } from 'react';
import { useShopifyStore } from '../../context/store-context';
import type { FavouriteProduct, Option } from '../../types/types';
import { formatPrice } from '../../utils/format';
import { percentage } from '../../utils/percentage';
import {
  unavailableOptionValues,
  variantForOptions
} from '../../utils/shopify-helper';
import Badge from '../badge/badge';
import Button from '../button/button';
import Icon from '../icon';
import VariantSelector from '../variant-selector/variant-selector';
import styles from './favourite-item.module.scss';

interface Props {
  product: FavouriteProduct;
}

const defaultShopifyOptionName = 'Title';

export default function FavouriteItem({ product }: Props) {
  const {
    state: { adding },
    addLineItem,
    setLastItemAdded,
    toggleProductInFavourites
  } = useShopifyStore();

  // Preselect the first size in stock (the first one, if all of them are sold out).
  // The availability is the runtime one, the favourite products are fetched in the browser.
  const [selectedVariant, setSelectedVariant] = useState(
    () =>
      product.variants.nodes.find(variant => variant.availableForSale) ??
      product.variants.nodes[0]
  );
  // option-name: value object of the selected variant, e.g. { Size: "M" }
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string | null>
  >(() =>
    Object.fromEntries(
      selectedVariant.selectedOptions.map(({ name, value }) => [name, value])
    )
  );

  const price = Number(selectedVariant.price.amount);
  const compareAtPrice = Number(selectedVariant.compareAtPrice?.amount ?? 0);
  const hasCompareAtPrice = compareAtPrice > price;
  const isAvailable = selectedVariant.availableForSale;

  const handleOptionChange = (option: Option, value: string) => {
    const so = { ...selectedOptions, [option.name]: value };
    const selectedVariantForOption = variantForOptions(
      product.variants.nodes,
      so
    );

    if (selectedVariantForOption) {
      setSelectedVariant(selectedVariantForOption);
      setSelectedOptions(so);
    }
  };

  const getDiscount = () => `-${100 - percentage(price, compareAtPrice)}%`;

  const addToCart = async () => {
    try {
      await addLineItem(selectedVariant.id);
      setLastItemAdded({
        productTitle: product.title,
        variantTitle: selectedVariant.title,
        price: selectedVariant.price.amount,
        imageUrl: product.featuredImage?.url
      });
      // a product moved to the cart is no longer kept among the favourites
      toggleProductInFavourites(product.id);
    } catch (error) {
      console.error('Failed to add product to the cart', error);
    }
  };

  return (
    <div className={styles.favouriteItem}>
      <Link to="/product/$handle" params={{ handle: product.handle }}>
        {product.featuredImage && (
          <Image
            src={product.featuredImage.url}
            layout="fullWidth"
            aspectRatio={1}
            background="var(--bs-tertiary-bg)"
            alt={`${product.title} karkötő`}
          />
        )}
      </Link>
      <div className={styles.productInfo}>
        <Link to="/product/$handle" params={{ handle: product.handle }}>
          <h3 className={styles.productTitle}>{product.title}</h3>
        </Link>
        <h4
          className={`${styles.productPrices} ${
            hasCompareAtPrice ? styles.hasCompareAtPrice : ''
          }`}
        >
          <span
            className={styles.productPrice}
            aria-label={hasCompareAtPrice ? 'Akciós ár' : 'Ár'}
          >
            {formatPrice(price)}
          </span>
          {hasCompareAtPrice && (
            <span
              className={styles.productCompareAtPrice}
              aria-label="Eredeti ár"
            >
              {formatPrice(compareAtPrice)}
            </span>
          )}
        </h4>
        <div className={styles.productVariant}>
          <div>
            {product.options
              .filter(option => option.name !== defaultShopifyOptionName)
              .map(option => (
                <VariantSelector
                  key={option.id}
                  option={option}
                  selected={selectedOptions[option.name]}
                  // e.g. sold out sizes can't be selected
                  unavailableValues={unavailableOptionValues(
                    product.variants.nodes,
                    option,
                    selectedOptions,
                    variant => variant.availableForSale
                  )}
                  onOptionChange={handleOptionChange}
                  showLabel={false}
                />
              ))}
          </div>
          <Button
            className={styles.buyButton}
            onClick={addToCart}
            disabled={adding || !isAvailable}
          >
            <Icon
              className={styles.buyButtonIcon}
              name="shopping-bag"
              size={20}
            />
            <span className={styles.buyButtonText}>
              {isAvailable ? 'Kosárba' : 'Nincs készleten'}
            </span>
          </Button>
        </div>
      </div>
      {hasCompareAtPrice && (
        <Badge className={styles.discountBadge}>{getDiscount()}</Badge>
      )}
      <Button
        className={styles.removeButton}
        variant="tertiary"
        onClick={() => toggleProductInFavourites(product.id)}
        aria-label="Eltávolítás a kedvencekből"
      >
        <Icon name="x" size={20} aria-hidden="true" />
      </Button>
    </div>
  );
}
